import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Check, X, LogOut } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// OAuth consent screen for the hosted MCP endpoint. The backend's
// /api/oauth/authorize validates the client and PKCE parameters, parks the
// request and redirects here with ?request=<id>. The signed-in user
// approves or denies; the backend answers with the redirect back to the
// client (Claude) carrying either a one-time code or access_denied.

const CAPABILITIES = [
  'Search and read leads, their timeline notes, emails and messages',
  'Read pipeline stages, tasks and sales reports',
  'Create and update leads, move stages, add notes, tasks and PDF documents',
  'Send emails and SMS to leads when you ask it to',
  'Set up the AI phone receptionist (admins with the Phone Agent add-on). It cannot buy phone numbers',
];

const OAuthConsent = () => {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const requestId = searchParams.get('request') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const framed = typeof window !== 'undefined' && window.top !== window.self;

  useEffect(() => {
    if (!isAuthenticated || !requestId || framed) return;
    let cancelled = false;
    api.get(`/oauth/requests/${encodeURIComponent(requestId)}`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not load this authorization request.');
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, requestId, framed]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api.post(`/oauth/requests/${encodeURIComponent(requestId)}/decision`, { approve });
      window.location.assign(r.data.redirectUrl);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Start the connection again from Claude.');
      setBusy(false);
    }
  };

  const switchAccount = async () => {
    const next = `${location.pathname}${location.search}`;
    await logout();
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  };

  let body;
  if (framed) {
    body = <p className="text-sm text-red-600">For your security this page cannot be shown inside another site. Open it in a new browser tab.</p>;
  } else if (!requestId) {
    body = <p className="text-sm text-gray-600">This link is missing its authorization request. Start the connection again from Claude.</p>;
  } else if (error) {
    body = <p className="text-sm text-red-600">{error}</p>;
  } else if (!info) {
    body = <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  } else {
    body = (
      <>
        <h1 className="text-xl font-bold text-gray-900 break-words">
          {info.client.name} wants to access your CRM
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          It will act as <strong>{info.user.name}</strong> ({info.user.email})
          {info.organization.name ? <> in <strong>{info.organization.name}</strong></> : null}, with
          the same permissions your account has.
        </p>

        {info.verifiedClaude ? (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
            <ShieldCheck size={14} /> Returns to Claude ({info.redirectHost})
          </p>
        ) : (
          <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>Returns to an app on this computer ({info.redirectHost}). Only allow it if you just started this connection yourself, for example from Claude Code.</span>
          </p>
        )}

        <div className="mt-5">
          <p className="text-sm font-medium text-gray-800 mb-2">It will be able to:</p>
          <ul className="space-y-1.5">
            {CAPABILITIES.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-gray-600">
                <Check size={16} className="text-primary-600 flex-shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            Lead records can include personal and health information. You can disconnect at any time
            under Settings → Connect Claude.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => decide(true)}
            disabled={busy}
            className="flex-1 min-w-[8rem] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            <Check size={16} /> Allow
          </button>
          <button
            onClick={() => decide(false)}
            disabled={busy}
            className="flex-1 min-w-[8rem] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded-lg text-sm font-semibold"
          >
            <X size={16} /> Deny
          </button>
        </div>
        <button
          onClick={switchAccount}
          className="mt-4 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <LogOut size={12} /> Not {user?.name}? Use another account
        </button>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-xl shadow-lg">
            <img src="/logo.png" alt="Gain Rhino" className="w-10 h-10 object-contain" />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">{body}</div>
        <p className="text-center text-xs text-gray-500 mt-4">
          <a href="/privacy" className="hover:underline">Privacy</a>
          {' · '}
          <a href="/integrations/claude" className="hover:underline">How the Claude connection works</a>
        </p>
      </div>
    </div>
  );
};

export default OAuthConsent;
